import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import {
  Loader2, FileText, Send, CheckCircle2, Clock, AlertTriangle,
  Download, BellOff, Trash2, RefreshCw, Upload, X, Paperclip, History, RotateCw
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABELS = {
  pending_invoice: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: Clock },
  invoice_sent: { label: 'Facture envoyée', color: 'bg-blue-100 text-blue-800 border-blue-200', icon: Send },
  paid: { label: 'Payé', color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle2 },
};

const fmtDate = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleDateString('fr-CH', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return iso; }
};

const fmtDateTime = (iso) => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    const ss = String(d.getSeconds()).padStart(2, '0');
    return `${dd}.${mm}.${yyyy} ${hh}:${mi}:${ss}`;
  } catch { return iso; }
};

const InvoicePaymentsPage = () => {
  const { token } = useAuth();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [counters, setCounters] = useState({ pending_invoice: 0, invoice_sent: 0, paid_this_month: 0 });
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);

  // Modals
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [paymentRef, setPaymentRef] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [actionLoading, setActionLoading] = useState(false);

  // Send-invoice dialog state
  const [pdfFile, setPdfFile] = useState(null);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [sendError, setSendError] = useState('');
  const [dragOver, setDragOver] = useState(false);

  const DEFAULT_BODY = (record) => {
    const fn = `${record?.customer?.first_name || ''}`.trim();
    return (
      `Veuillez trouver ci-joint la facture pour votre abonnement CarMaintenance.\n\n` +
      `Cette première facture est à régler dès réception (à l'aide du QR-bill joint).\n` +
      `Votre abonnement sera activé dès la confirmation du paiement, et votre contrat vous sera envoyé automatiquement.\n\n` +
      `Pour toute question, écrivez-nous à info@car-main.ch.${fn ? `\n\nBien à vous,` : ''}`
    );
  };

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === 'all' ? {} : { status: filter };
      const { data } = await axios.get(`${API}/admin/invoice-payments`, { ...authHeaders, params });
      setRecords(data.records || []);
      setCounters(data.counters || { pending_invoice: 0, invoice_sent: 0, paid_this_month: 0 });
    } catch (err) {
      toast({ variant: 'destructive', title: 'Erreur', description: 'Impossible de charger les dossiers' });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, token]);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  const openSendDialog = (record) => {
    setSelected(record);
    setPaymentRef(record.payment_reference || '');
    setPdfFile(null);
    setSendError('');
    const ref = record.payment_reference || record.id?.slice(0, 8) || '';
    setEmailSubject(`Votre facture CarMaintenance${ref ? ` — n°${ref}` : ''}`);
    setEmailBody(DEFAULT_BODY(record));
    setSendDialogOpen(true);
  };

  const openConfirmDialog = (record) => {
    setSelected(record);
    setPaymentRef(record.payment_reference || '');
    setPaymentDate(new Date().toISOString().split('T')[0]);
    setConfirmDialogOpen(true);
  };

  const handlePickFile = (f) => {
    setSendError('');
    if (!f) return;
    if (f.type !== 'application/pdf' && !f.name.toLowerCase().endsWith('.pdf')) {
      setSendError('Le fichier doit être un PDF.');
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      setSendError('Le PDF dépasse 10 Mo.');
      return;
    }
    setPdfFile(f);
  };

  const handleSendInvoice = async () => {
    if (!selected || !pdfFile) return;
    setActionLoading(true);
    setSendError('');
    try {
      const fd = new FormData();
      fd.append('file', pdfFile);
      fd.append('subject', emailSubject);
      fd.append('message', emailBody);
      fd.append('to_email', selected.customer?.email || '');
      if (paymentRef) fd.append('payment_reference', paymentRef);

      await axios.post(
        `${API}/admin/invoice-payments/${selected.id}/send-invoice`,
        fd,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast({ title: 'Facture envoyée', description: `Email + PDF transmis à ${selected.customer?.email}.` });
      setSendDialogOpen(false);
      setPdfFile(null);
      fetchRecords();
    } catch (err) {
      const msg = err.response?.data?.detail || "Erreur lors de l'envoi";
      setSendError(msg);
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkSent = async () => {
    if (!selected) return;
    setActionLoading(true);
    try {
      await axios.patch(
        `${API}/admin/invoice-payments/${selected.id}/mark-invoice-sent`,
        { payment_reference: paymentRef || null },
        authHeaders
      );
      toast({ title: 'Statut mis à jour', description: 'Facture marquée comme envoyée. Email envoyé au client.' });
      setSendDialogOpen(false);
      fetchRecords();
    } catch (err) {
      toast({ variant: 'destructive', title: 'Erreur', description: err.response?.data?.detail || 'Erreur lors de la mise à jour' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmPayment = async () => {
    if (!selected) return;
    setActionLoading(true);
    try {
      const { data: resp } = await axios.patch(
        `${API}/admin/invoice-payments/${selected.id}/confirm-payment`,
        { payment_received_date: new Date(paymentDate).toISOString(), payment_reference: paymentRef || null },
        authHeaders
      );
      const isFirst = resp?.payment_type === 'first';
      toast({
        title: isFirst ? 'Premier paiement confirmé' : 'Paiement enregistré',
        description: isFirst
          ? 'Contrat généré et envoyé au client par email.'
          : 'E-mail de confirmation envoyé au client.',
      });
      setConfirmDialogOpen(false);
      fetchRecords();
    } catch (err) {
      toast({ variant: 'destructive', title: 'Erreur', description: err.response?.data?.detail || 'Erreur lors de la confirmation' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleResendContract = async (record) => {
    if (!window.confirm("Renvoyer le contrat par e-mail au client ?")) return;
    try {
      await axios.post(`${API}/admin/invoice-payments/${record.id}/resend-contract`, {}, authHeaders);
      toast({ title: 'Contrat renvoyé', description: `Email transmis à ${record.customer?.email}.` });
      fetchRecords();
    } catch (err) {
      toast({ variant: 'destructive', title: 'Erreur', description: err.response?.data?.detail || 'Échec du renvoi du contrat' });
    }
  };

  const handleAdvanceCycle = async (record) => {
    const next = (record.billing_cycle || 1) + 1;
    const total = record.total_cycles || record.subscription?.commitment_months || '?';
    if (!window.confirm(`Générer manuellement le cycle ${next} / ${total} ? Le dossier repassera en "En attente" pour la nouvelle facture.`)) return;
    try {
      const { data } = await axios.post(`${API}/admin/invoice-payments/${record.id}/advance-cycle`, {}, authHeaders);
      toast({ title: 'Cycle ouvert', description: data.message });
      fetchRecords();
    } catch (err) {
      toast({ variant: 'destructive', title: 'Erreur', description: err.response?.data?.detail || 'Impossible de générer le cycle suivant' });
    }
  };

  const handleDismissReminder = async (record) => {
    try {
      await axios.patch(`${API}/admin/invoice-payments/${record.id}/dismiss-reminder`, {}, authHeaders);
      toast({ title: 'Alerte désactivée' });
      fetchRecords();
    } catch (err) {
      toast({ variant: 'destructive', title: 'Erreur', description: 'Impossible de désactiver l\'alerte' });
    }
  };

  const handleDelete = async (record) => {
    if (!window.confirm(`Supprimer le dossier de ${record.customer.first_name} ${record.customer.last_name} ?`)) return;
    try {
      await axios.delete(`${API}/admin/invoice-payments/${record.id}`, authHeaders);
      toast({ title: 'Dossier supprimé' });
      fetchRecords();
    } catch (err) {
      toast({ variant: 'destructive', title: 'Erreur', description: err.response?.data?.detail || 'Erreur lors de la suppression' });
    }
  };

  const handleExportCsv = async () => {
    try {
      const response = await axios.get(`${API}/admin/invoice-payments/export-csv`, {
        ...authHeaders,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const now = new Date();
      const filename = `paiements-facture-${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}.csv`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast({ title: 'Export terminé', description: 'Le fichier CSV a été téléchargé' });
    } catch (err) {
      toast({ variant: 'destructive', title: 'Erreur', description: 'Échec de l\'export CSV' });
    }
  };

  return (
    <div data-testid="invoice-payments-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FileText className="w-6 h-6 text-[#f59e0b]" />
            Paiements par facture
          </h1>
          <p className="text-sm text-gray-500 mt-1">Gérez les abonnements payés par facture QR</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchRecords} disabled={loading} data-testid="invoice-payments-refresh-button">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Rafraîchir
          </Button>
          <Button onClick={handleExportCsv} className="bg-gray-900 hover:bg-gray-800 text-white" data-testid="invoice-payments-export-csv-button">
            <Download className="w-4 h-4 mr-2" />
            Export CSV (mois)
          </Button>
        </div>
      </div>

      {/* Counters */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <CounterCard label="En attente" value={counters.pending_invoice} icon={Clock} color="text-yellow-600 bg-yellow-50" testId="counter-pending" />
        <CounterCard label="Facture envoyée" value={counters.invoice_sent} icon={Send} color="text-blue-600 bg-blue-50" testId="counter-invoice-sent" />
        <CounterCard label="Payé ce mois" value={counters.paid_this_month} icon={CheckCircle2} color="text-green-600 bg-green-50" testId="counter-paid-this-month" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        {[
          { key: 'all', label: 'Tous' },
          { key: 'pending_invoice', label: 'En attente' },
          { key: 'invoice_sent', label: 'Facture envoyée' },
          { key: 'paid', label: 'Payés' },
        ].map(f => (
          <Button
            key={f.key}
            size="sm"
            variant={filter === f.key ? 'default' : 'outline'}
            onClick={() => setFilter(f.key)}
            className={filter === f.key ? 'bg-[#52a675] hover:bg-[#3d8a5c]' : ''}
            data-testid={`filter-${f.key}`}
          >
            {f.label}
          </Button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-20"><Loader2 className="w-8 h-8 mx-auto animate-spin text-[#52a675]" /></div>
      ) : records.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-gray-500">Aucun dossier trouvé</CardContent></Card>
      ) : (
        <div className="space-y-4">
          {records.map((r) => (
            <InvoiceRecordCard
              key={r.id}
              record={r}
              onMarkSent={() => openSendDialog(r)}
              onConfirmPayment={() => openConfirmDialog(r)}
              onDismissReminder={() => handleDismissReminder(r)}
              onDelete={() => handleDelete(r)}
              onResendContract={() => handleResendContract(r)}
              onAdvanceCycle={() => handleAdvanceCycle(r)}
            />
          ))}
        </div>
      )}

      {/* Send Invoice Dialog */}
      <Dialog open={sendDialogOpen} onOpenChange={(v) => { if (!actionLoading) setSendDialogOpen(v); }}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="dialog-send-invoice">
          <DialogHeader>
            <DialogTitle>Envoyer la facture par email</DialogTitle>
            <DialogDescription>
              Uploadez le PDF de la facture, vérifiez l'email et envoyez-le directement au client.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* File drop zone */}
            <div>
              <Label className="text-sm">Fichier PDF de la facture <span className="text-red-500">*</span></Label>
              {!pdfFile ? (
                <label
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={(e) => { e.preventDefault(); setDragOver(false); handlePickFile(e.dataTransfer.files?.[0]); }}
                  className={`mt-1.5 flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-xl py-8 cursor-pointer transition-colors ${
                    dragOver ? 'border-[#52a675] bg-[#52a675]/5' : 'border-gray-300 hover:border-[#52a675]/60 hover:bg-gray-50'
                  }`}
                  data-testid="invoice-drop-zone"
                >
                  <Upload className="w-8 h-8 text-gray-400" />
                  <p className="text-sm text-gray-700 font-medium">Cliquez ou déposez votre PDF ici</p>
                  <p className="text-xs text-gray-500">Format : PDF, taille max 10 Mo</p>
                  <input
                    type="file"
                    accept="application/pdf,.pdf"
                    className="hidden"
                    onChange={(e) => handlePickFile(e.target.files?.[0])}
                    data-testid="invoice-file-input"
                  />
                </label>
              ) : (
                <div className="mt-1.5 flex items-center justify-between gap-3 p-3 bg-[#52a675]/5 border border-[#52a675]/20 rounded-lg" data-testid="invoice-selected-file">
                  <div className="flex items-center gap-2 min-w-0">
                    <Paperclip className="w-4 h-4 text-[#52a675] shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{pdfFile.name}</p>
                      <p className="text-xs text-gray-500">{(pdfFile.size / 1024).toFixed(0)} Ko</p>
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => setPdfFile(null)} data-testid="invoice-remove-file">
                    <X className="w-4 h-4 mr-1" /> Retirer
                  </Button>
                </div>
              )}
            </div>

            {/* Recipient (readonly) */}
            <div className="space-y-1.5">
              <Label className="text-sm">Destinataire</Label>
              <Input
                value={selected?.customer?.email || ''}
                readOnly
                className="bg-gray-50 cursor-not-allowed"
                data-testid="invoice-recipient"
              />
            </div>

            {/* Subject */}
            <div className="space-y-1.5">
              <Label htmlFor="invoice-subject" className="text-sm">Objet de l'email</Label>
              <Input
                id="invoice-subject"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                data-testid="invoice-subject"
              />
            </div>

            {/* Body */}
            <div className="space-y-1.5">
              <Label htmlFor="invoice-body" className="text-sm">Message</Label>
              <textarea
                id="invoice-body"
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
                rows={7}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#52a675]/30 focus:border-[#52a675] resize-none"
                data-testid="invoice-body"
              />
            </div>

            {/* Payment reference (optional) */}
            <div className="space-y-1.5">
              <Label htmlFor="invoice-payment-ref" className="text-sm">Référence de paiement (optionnelle)</Label>
              <Input
                id="invoice-payment-ref"
                placeholder="Ex: N° facture QR-IBAN"
                value={paymentRef}
                onChange={(e) => setPaymentRef(e.target.value)}
                data-testid="invoice-payment-ref"
              />
            </div>

            {sendError && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md p-3" data-testid="invoice-send-error">
                {sendError}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSendDialogOpen(false)} disabled={actionLoading}>Annuler</Button>
            <Button
              onClick={handleSendInvoice}
              disabled={actionLoading || !pdfFile || !emailSubject.trim() || !emailBody.trim()}
              className="bg-[#52a675] hover:bg-[#3d8a5c]"
              data-testid="dialog-send-invoice-confirm"
            >
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Envoyer la facture
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Payment Dialog */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent data-testid="dialog-confirm-payment">
          <DialogHeader>
            <DialogTitle>
              {selected?.payment_history?.length
                ? 'Enregistrer un paiement récurrent'
                : 'Confirmer le 1er paiement reçu'}
            </DialogTitle>
            <DialogDescription>
              {selected?.payment_history?.length
                ? `Le paiement mensuel de ${selected?.customer?.first_name} ${selected?.customer?.last_name} sera enregistré et un e-mail de confirmation lui sera envoyé.`
                : `L'abonnement de ${selected?.customer?.first_name} ${selected?.customer?.last_name} sera activé. Son contrat sera généré automatiquement et envoyé par e-mail avec la confirmation de paiement.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="payment-ref-confirm">Référence de paiement</Label>
              <Input
                id="payment-ref-confirm"
                placeholder="Ex: N° facture QR-IBAN"
                value={paymentRef}
                onChange={(e) => setPaymentRef(e.target.value)}
                data-testid="payment-reference-input-confirm"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="payment-date">Date du paiement reçu</Label>
              <Input
                id="payment-date"
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                data-testid="payment-date-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialogOpen(false)} disabled={actionLoading}>Annuler</Button>
            <Button onClick={handleConfirmPayment} disabled={actionLoading} className="bg-green-600 hover:bg-green-700" data-testid="dialog-confirm-payment-button">
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Confirmer le paiement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const CounterCard = ({ label, value, icon: Icon, color, testId }) => (
  <Card data-testid={testId}>
    <CardContent className="p-5">
      <div className="flex items-center gap-4">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${color}`}>
          <Icon className="w-6 h-6" />
        </div>
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className="text-3xl font-bold text-gray-900">{value}</p>
        </div>
      </div>
    </CardContent>
  </Card>
);

const InvoiceRecordCard = ({ record, onMarkSent, onConfirmPayment, onDismissReminder, onDelete, onResendContract, onAdvanceCycle }) => {
  const statusCfg = STATUS_LABELS[record.status] || STATUS_LABELS.pending_invoice;
  const StatusIcon = statusCfg.icon;
  const c = record.customer || {};
  const v = record.vehicle || {};
  const s = record.subscription || {};
  const fullName = `${c.first_name || ''} ${c.last_name || ''}`.trim();

  const overdueClass = record.is_overdue ? 'border-l-4 border-l-red-500 bg-red-50/30' : '';
  const currentCycle = record.billing_cycle || 1;
  const totalCycles = record.total_cycles || s.commitment_months || '?';
  const engagementReached = totalCycles !== '?' && currentCycle >= totalCycles;
  const statusHistory = Array.isArray(record.status_history) ? record.status_history : [];

  return (
    <Card className={`overflow-hidden ${overdueClass}`} data-testid={`invoice-record-${record.id}`}>
      <CardHeader className="bg-gray-50 pb-3 flex flex-row items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3 flex-wrap">
          <CardTitle className="text-lg" data-testid="record-customer-name">{fullName}</CardTitle>
          <Badge className={`${statusCfg.color} border px-2 py-0.5 inline-flex items-center gap-1`} data-testid={`record-status-${record.id}`}>
            <StatusIcon className="w-3 h-3" /> {statusCfg.label}
          </Badge>
          <Badge
            className="bg-indigo-50 text-indigo-700 border border-indigo-200 px-2 py-0.5 inline-flex items-center gap-1"
            data-testid={`record-cycle-${record.id}`}
            title="Cycle de facturation courant / engagement total"
          >
            <RotateCw className="w-3 h-3" /> Cycle {currentCycle} / {totalCycles}
          </Badge>
          {record.is_overdue && (
            <Badge className="bg-red-100 text-red-800 border border-red-200 px-2 py-0.5 inline-flex items-center gap-1" data-testid="record-overdue-badge">
              <AlertTriangle className="w-3 h-3" /> + 30 jours
            </Badge>
          )}
        </div>
        <div className="text-xs text-gray-500" data-testid="record-created-at">
          Créé le {fmtDate(record.created_at)}
        </div>
      </CardHeader>
      <CardContent className="pt-4 grid md:grid-cols-3 gap-4 text-sm">
        <div className="space-y-1">
          <p className="text-xs text-gray-500 uppercase">Client</p>
          <p><strong>{fullName}</strong></p>
          <p>{c.email}</p>
          <p>{c.phone}</p>
          <p className="text-gray-600">{c.address}, {c.zip_code} {c.city}</p>
        </div>
        <div className="space-y-1">
          <p className="text-xs text-gray-500 uppercase">Véhicule & Plan</p>
          <p><strong>{v.brand} {v.model || ''}</strong> ({v.year})</p>
          <p className="text-gray-600">Plaque : {v.plate_number || '-'}</p>
          <p>Plan <strong>{s.plan_code}</strong> · CHF {s.monthly_price?.toFixed(2)}/mois</p>
          <p className="text-gray-600">Engagement {s.commitment_months} mois</p>
        </div>
        <div className="space-y-1">
          <p className="text-xs text-gray-500 uppercase">Paiement</p>
          <p>Référence : <strong>{record.payment_reference || '-'}</strong></p>
          <p className="flex items-center gap-1.5 flex-wrap">
            <span>Facture envoyée : {fmtDate(record.invoice_sent_date)}</span>
            {record.legacy_manual_mark && (
              <Badge variant="outline" className="text-[10px] bg-gray-50 text-gray-600 border-gray-300 px-1.5 py-0">
                marqué manuellement
              </Badge>
            )}
          </p>
          <p>Paiement reçu : {fmtDate(record.payment_received_date)}</p>
          {record.contract_sent_at && (
            <p className="flex items-center gap-1.5 text-xs text-[#3d8a5c]" data-testid={`record-contract-status-${record.id}`}>
              <CheckCircle2 className="w-3.5 h-3.5" />
              Contrat envoyé le {fmtDate(record.contract_sent_at)}
            </p>
          )}
          {Array.isArray(record.payment_history) && record.payment_history.length > 0 && (
            <p className="text-xs text-gray-500">
              {record.payment_history.length} paiement{record.payment_history.length > 1 ? 's' : ''} enregistré{record.payment_history.length > 1 ? 's' : ''}
            </p>
          )}
        </div>
      </CardContent>

      {/* History of status transitions */}
      {statusHistory.length > 0 && (
        <div className="px-6 pb-3 border-t border-gray-100 pt-3" data-testid={`status-history-${record.id}`}>
          <p className="text-xs uppercase tracking-wide text-gray-500 mb-2 flex items-center gap-1.5">
            <History className="w-3.5 h-3.5" /> Historique des statuts
          </p>
          <ul className="space-y-1 max-h-48 overflow-y-auto pr-2">
            {statusHistory.map((h, idx) => {
              const cfg = STATUS_LABELS[h.status] || { label: h.status, color: 'bg-gray-100 text-gray-700 border-gray-200' };
              return (
                <li
                  key={`${h.at}-${idx}`}
                  className="text-xs text-gray-700 flex flex-wrap items-center gap-2 font-mono"
                  data-testid={`status-history-entry-${record.id}-${idx}`}
                >
                  <span className="text-gray-500">{fmtDateTime(h.at)}</span>
                  <span className="text-gray-400">:</span>
                  <span className="px-1.5 py-0.5 rounded text-[10px] bg-indigo-50 text-indigo-700 border border-indigo-200">cycle {h.cycle}</span>
                  <span className={`px-1.5 py-0.5 rounded text-[10px] border ${cfg.color}`}>{cfg.label || h.status}</span>
                  {h.by && h.by !== 'system' && h.by !== 'system_auto' && h.by !== 'backfill' && (
                    <span className="text-gray-500 not-italic font-sans">par {h.by}</span>
                  )}
                  {h.by === 'system_auto' && <span className="text-gray-500 not-italic font-sans">(auto)</span>}
                  {h.by === 'backfill' && <span className="text-gray-400 not-italic font-sans">(rétroactif)</span>}
                  {h.note && <span className="text-gray-500 italic font-sans">— {h.note}</span>}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* History of invoice sends */}
      {Array.isArray(record.invoice_send_history) && record.invoice_send_history.length > 0 && (
        <div className="px-6 pb-3 border-t border-gray-100 pt-3" data-testid={`invoice-history-${record.id}`}>
          <p className="text-xs uppercase tracking-wide text-gray-500 mb-2 flex items-center gap-1.5">
            <History className="w-3.5 h-3.5" /> Historique d'envoi
          </p>
          <ul className="space-y-1.5">
            {record.invoice_send_history.map((h) => (
              <li key={h.id} className="text-xs text-gray-700 flex flex-wrap items-center gap-2">
                <span className="font-mono">{fmtDate(h.sent_at)}</span>
                <span className="text-gray-500">·</span>
                <span>{h.to_email}</span>
                <span className="text-gray-500">·</span>
                <span className="italic text-gray-600 truncate max-w-[260px]" title={h.subject}>{h.subject}</span>
                <a
                  href={`${API}/admin/invoice-payments/${record.id}/attachment/${h.attachment_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    e.preventDefault();
                    const t = localStorage.getItem('admin_token');
                    fetch(e.currentTarget.href, { headers: { Authorization: `Bearer ${t}` } })
                      .then((r) => r.blob())
                      .then((b) => {
                        const url = URL.createObjectURL(b);
                        const a = document.createElement('a');
                        a.href = url; a.download = h.filename || 'facture.pdf'; a.click();
                        URL.revokeObjectURL(url);
                      });
                  }}
                  className="text-[#52a675] hover:underline inline-flex items-center gap-1"
                  data-testid={`invoice-history-download-${h.id}`}
                >
                  <Download className="w-3 h-3" /> Télécharger
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="px-6 pb-4 flex flex-wrap gap-2 justify-end border-t border-gray-100 pt-3">
        {record.is_overdue && (
          <Button size="sm" variant="outline" onClick={onDismissReminder} data-testid={`dismiss-reminder-${record.id}`}>
            <BellOff className="w-4 h-4 mr-2" /> Désactiver l'alerte
          </Button>
        )}
        {record.contract_attachment_id && (
          <Button
            size="sm"
            variant="outline"
            onClick={onResendContract}
            className="border-[#52a675]/40 text-[#3d8a5c] hover:bg-[#52a675]/5"
            data-testid={`resend-contract-${record.id}`}
          >
            <Send className="w-4 h-4 mr-2" /> Renvoyer le contrat
          </Button>
        )}
        {!engagementReached && (
          <Button
            size="sm"
            variant="outline"
            onClick={onAdvanceCycle}
            className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
            data-testid={`advance-cycle-${record.id}`}
            title="Ouvrir maintenant le prochain cycle de facturation"
          >
            <RotateCw className="w-4 h-4 mr-2" /> Cycle suivant
          </Button>
        )}
        {(record.status === 'pending_invoice' || record.invoice_send_history?.length > 0) && (
          <Button size="sm" onClick={onMarkSent} className="bg-[#52a675] hover:bg-[#3d8a5c] text-white" data-testid={`send-invoice-${record.id}`}>
            <Send className="w-4 h-4 mr-2" />
            {record.invoice_send_history?.length > 0 ? 'Renvoyer la facture' : 'Envoyer la facture'}
          </Button>
        )}
        <Button size="sm" onClick={onConfirmPayment} className="bg-green-600 hover:bg-green-700 text-white" data-testid={`confirm-payment-${record.id}`}>
          <CheckCircle2 className="w-4 h-4 mr-2" />
          {record.status === 'paid' ? 'Enregistrer un paiement' : 'Confirmer paiement reçu'}
        </Button>
        {record.status !== 'paid' && (
          <Button size="sm" variant="ghost" onClick={onDelete} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`delete-record-${record.id}`}>
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>
    </Card>
  );
};

export default InvoicePaymentsPage;
